import dayjs from "dayjs";
import "dayjs/locale/ko";
import { AiOutlineClose } from "react-icons/ai";

import ModalLayout from "@/components/Layout/ModalLayout";
import { RESERVATION_STATE } from "@/constants/common";
import useCalendarStore from "@/store/calendarStore";
import type { DailyReservationData } from "@/types/reservation";
import { cn } from "@/utils/cn";

dayjs.locale("ko");

const statusStyle: Record<string, string> = {
  approve: "bg-green-100 text-green-700",
  request: "bg-amber-50 text-amber-600",
  reject: "bg-red-50 text-red-600",
  cancel: "bg-gray-100 text-gray-500",
};

const formatTime = (t: string) => {
  if (!t || t.length < 4) return "";
  return `${t.slice(0, 2)}:${t.slice(2, 4)}`;
};

interface ReservationDetailModalProps {
  reservation: DailyReservationData;
  onClose: () => void;
}

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-baseline border-b border-gray-100 py-3 text-sm last:border-b-0">
    <div className="w-20 shrink-0 font-medium text-gray-500">{label}</div>
    <div className="text-gray-800">{children}</div>
  </div>
);

const ReservationDetailModal = ({ reservation, onClose }: ReservationDetailModalProps) => {
  const { rsvtDt, startTime, endTime, plcNm, useCnts, userName, telNo, sttCd } = reservation;
  const storeDate = useCalendarStore(state => state.date);

  const dateFormatted = rsvtDt && rsvtDt.length >= 8
    ? dayjs(`${rsvtDt.slice(0, 4)}-${rsvtDt.slice(4, 6)}-${rsvtDt.slice(6, 8)}`).format("YYYY년 M월 D일 (dd)")
    : storeDate.format("YYYY년 M월 D일 (dd)");

  return (
    <ModalLayout variant="primary" onClose={onClose}>
      <div className="flex w-[340px] flex-col sm:w-[420px]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h3 className="text-base font-bold text-gray-900">예약 상세</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <AiOutlineClose size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <div className="flex flex-col">
            <InfoRow label="상태">
              <span className={cn(
                "rounded-full px-2.5 py-0.5 text-xs font-medium",
                statusStyle[sttCd] || statusStyle.approve,
              )}>
                {RESERVATION_STATE[sttCd] || sttCd}
              </span>
            </InfoRow>
            <InfoRow label="날짜">{dateFormatted}</InfoRow>
            <InfoRow label="시간">
              {formatTime(startTime)} ~ {formatTime(endTime)}
            </InfoRow>
            <InfoRow label="장소">{plcNm}</InfoRow>
            <InfoRow label="사용 목적">{useCnts}</InfoRow>
            <InfoRow label="예약자">{userName || "-"}</InfoRow>
            <InfoRow label="연락처">{telNo || "-"}</InfoRow>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            확인
          </button>
        </div>
      </div>
    </ModalLayout>
  );
};

export default ReservationDetailModal;
